import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { api, errorMessage } from '../../api/client.js'
import { meQuery } from '../../api/queries.js'
import { schemas } from '../../api/resources.js'
import { useT } from '../../i18n/index.js'

/** Landing page of a one-time link from the super console: exchanges it for a session, then opens the dashboard. */
export function SsoPage() {
  const { token } = useSearch({ from: '/sso' })
  return <TokenSignIn endpoint="/auth/sso" token={token} invalidKey="auth.sso.invalid" />
}

/**
 * Landing page of the handoff from admin.digimasi.com (#228 step 3). The one-time token comes in the address fragment,
 * which never reaches a server; it is removed from the address bar before it is used.
 */
export function CentralPage() {
  // Read without side effects (strict mode runs this twice), then cleared from the address bar.
  const [token] = useState(() => {
    const hash = window.location.hash.slice(1)
    return /^[0-9a-f]{64}$/.test(hash) ? hash : undefined
  })
  useEffect(() => {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname)
  }, [])
  return <TokenSignIn endpoint="/auth/central" token={token} invalidKey="auth.central.invalid" />
}

function TokenSignIn({ endpoint, token, invalidKey }: { endpoint: '/auth/sso' | '/auth/central'; token: string | undefined; invalidKey: 'auth.sso.invalid' | 'auth.central.invalid' }) {
  const t = useT()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // React strict mode runs effects twice in development; the link works once.
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    if (!token) return setError(t(invalidKey))
    api(endpoint, schemas.me, { method: 'POST', body: { token } })
      .then(async (me) => {
        qc.setQueryData(meQuery.queryKey, me)
        await navigate({ to: '/', replace: true })
      })
      .catch((err: unknown) => setError(errorMessage(err)))
  }, [token, qc, navigate, t, endpoint, invalidKey])

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--mv-bg)] px-4">
      <div className="mv-panel w-full max-w-sm !p-6 text-center">
        {error ? (
          <>
            <h1 className="font-[family-name:var(--mv-font-display)] text-xl font-bold text-[var(--mv-fg-strong)]">{t('auth.sso.failedTitle')}</h1>
            <p className="mt-2 text-sm text-[var(--fg-muted)]">{error}</p>
            <Link to="/login" className="mt-4 inline-block text-sm font-medium text-[var(--mv-blue)]">
              {t('auth.sso.toLogin')}
            </Link>
          </>
        ) : (
          <p className="text-sm text-[var(--fg-muted)]" role="status">
            {t('auth.sso.signingIn')}
          </p>
        )}
      </div>
    </div>
  )
}
